import type { SurveyCardAppearance } from "@weloop/core/widget/survey-card-frame";

import {
	SURVEY_CARD_FRAME_PATH,
	SURVEY_CARD_MESSAGE,
} from "@weloop/core/widget/survey-card-frame";

import type { EmbeddedSurvey } from "./survey-embed";

// `survey.mount(element)`: draws Weloop's survey card inside an element of
// the host page. The card lives in an iframe of the widget's `/survey-card`
// route, so it wears the project's widget styling and the page's CSS cannot
// reach it – the survey looks the way the dashboard previews it on every
// site. The frame only draws; answers go back through the survey handle, so
// the cooldown and impression bookkeeping are the embed API's own.

/**
 * What `mount` hands the frame's answers to – the survey handle's `answer`,
 * resolving with the widget's status when it has one (`sign_in_required`:
 * the answer is parked until the visitor signs in).
 */
export type SurveyCardAnswer = (
	input: Readonly<Record<string, unknown>>,
) => Promise<string | undefined>;

/** What `mount` hands the frame's drafts to – the answers so far, unconfirmed. */
export type SurveyCardDraft = (answers: unknown) => void;

/** Options of `survey.mount()`. Public API – add fields, never rename. */
export interface SurveyMountOptions {
	/**
	 * Called once the card is gone: the visitor answered and the thank-you
	 * card had its moment, or closed it with its cross. Use it to collapse the
	 * space the card took.
	 */
	readonly onClose?: () => void;
}

/** What `survey.mount()` returns. Public API – add fields, never rename. */
export interface MountedSurvey {
	/** Removes the card. Does not count as a dismissal – call `dismiss()` for that. */
	readonly unmount: () => void;
}

export interface MountSurveyCardInput {
	readonly answer: SurveyCardAnswer;
	readonly appearance: SurveyCardAppearance | undefined;
	/** The survey handle's `dismiss`: the user closed the card. */
	readonly dismiss: () => void;
	/** The card's answers so far, saved as a draft. */
	readonly draft: SurveyCardDraft;
	/**
	 * The host page's language hint, for a widget too old to send its own
	 * resolved language in `appearance`.
	 */
	readonly fallbackLocale: string | undefined;
	readonly options: SurveyMountOptions | undefined;
	/** The public survey, without the handle's functions – they cannot cross postMessage. */
	readonly survey: EmbeddedSurvey;
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
	readonly target: Element | string;
	readonly theme: "dark" | "light";
	readonly widgetOrigin: string;
}

// The frame opens at zero height and grows to the card once it reports its
// size, so the page never shows an empty box while the widget loads.
const FRAME_STYLE: Readonly<Record<string, string>> = {
	background: "transparent",
	border: "0",
	display: "block",
	height: "0",
	overflow: "hidden",
	width: "100%",
};

const isObject = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

/** The frame URL. Exported for tests. */
export const buildSurveyCardSrc = (
	widgetOrigin: string,
	locale: string | undefined,
	theme: "dark" | "light",
): string => {
	const url = new URL(SURVEY_CARD_FRAME_PATH, widgetOrigin);
	if (locale !== undefined) {
		url.searchParams.set("lang", locale);
	}
	url.searchParams.set("theme", theme);
	return url.toString();
};

// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
const resolveTarget = (target: Element | string): Element => {
	if (typeof target !== "string") {
		return target;
	}
	const element = document.querySelector(target);
	if (element === null) {
		throw new TypeError(
			`[weloop] survey.mount() found no element for "${target}"`,
		);
	}
	return element;
};

/** The answer the frame posted, reduced to what the survey handle takes. */
const answerInput = (
	message: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> =>
	message["answers"] === undefined
		? {
				comment: message["comment"],
				score: message["score"],
				...(message["isPartial"] === true ? { isPartial: true } : {}),
			}
		: { answers: message["answers"] };

// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- the input carries the target Element, an external mutable DOM interface
export function mountSurveyCard(input: MountSurveyCardInput): MountedSurvey {
	const container = resolveTarget(input.target);
	const iframe = document.createElement("iframe");
	// The widget already resolved the language: the signed-in user's own,
	// else the host app's. The card speaks the same one as the widget.
	iframe.src = buildSurveyCardSrc(
		input.widgetOrigin,
		input.appearance?.locale ?? input.fallbackLocale,
		input.theme,
	);
	// Named by the survey itself, already in the visitor's language – the
	// bundle carries no catalogue word for it.
	iframe.title = input.survey.title;
	Object.assign(iframe.style, FRAME_STYLE);
	// A frame whose color scheme differs from the page's gets an opaque
	// canvas; matching it keeps the card's corners on the page background.
	iframe.style.colorScheme = input.theme;
	container.append(iframe);

	const post = (message: Readonly<Record<string, unknown>>): void => {
		iframe.contentWindow?.postMessage(message, input.widgetOrigin);
	};

	// Aborting removes the message listener added at the end.
	const listening = new AbortController();
	const unmount = (): void => {
		if (listening.signal.aborted) {
			return;
		}
		listening.abort();
		iframe.remove();
	};

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MessageEvent is an external mutable DOM interface
	const onMessage = (event: MessageEvent): void => {
		const data = event.data as unknown;
		if (
			event.origin !== input.widgetOrigin ||
			event.source !== iframe.contentWindow ||
			!isObject(data)
		) {
			return;
		}
		switch (data["type"]) {
			case SURVEY_CARD_MESSAGE.answer: {
				const { requestId } = data;
				if (typeof requestId !== "string") {
					return;
				}
				void (async () => {
					try {
						const status = await input.answer(answerInput(data));
						post({
							ok: true,
							requestId,
							...(status === undefined ? {} : { status }),
							type: SURVEY_CARD_MESSAGE.result,
						});
					} catch {
						post({ ok: false, requestId, type: SURVEY_CARD_MESSAGE.result });
					}
				})();
				return;
			}
			// The card's cross: an explicit "not now". The handle records the
			// refusal – it is a no-op on the widget side once an answer stood.
			case SURVEY_CARD_MESSAGE.dismiss: {
				input.dismiss();
				unmount();
				input.options?.onClose?.();
				break;
			}
			case SURVEY_CARD_MESSAGE.done: {
				unmount();
				input.options?.onClose?.();
				return;
			}
			case SURVEY_CARD_MESSAGE.draft: {
				input.draft(data["answers"]);
				return;
			}
			case SURVEY_CARD_MESSAGE.ready: {
				post({
					appearance: input.appearance,
					survey: input.survey,
					type: SURVEY_CARD_MESSAGE.show,
				});
				return;
			}
			case SURVEY_CARD_MESSAGE.resize: {
				const { height } = data;
				if (typeof height === "number" && Number.isFinite(height)) {
					iframe.style.height = `${String(Math.max(0, height))}px`;
				}
				break;
			}
			default: {
				break;
			}
		}
	};

	window.addEventListener("message", onMessage, { signal: listening.signal });
	return { unmount };
}
